import { GraduationCap } from 'assets/icons/revamp';
import { DEFAULT_TEAM_NAME } from 'assets/data';
import {
  PROFILE_LANGUAGE_MENU_ID,
  PROFILE_SUBMENU_ID,
  PROFILE_THEME_MENU_ID
} from 'constants/navBar';
import { PRIVACY_POLICY, ROUTES } from 'constants/routes';
import { ATTRIB } from 'constants/test';
import { TEAM_ACCESS_ROLE } from 'enums';
import { MenuItem } from 'interfaces';
import { ClassInfo } from 'interfaces/classes.interface';
import { AdminForTeams, Team } from 'interfaces/team.interface';
import { t } from 'services/i18n.service';

type NavTeam = Pick<
  Team,
  'id' | 'name' | 'is_class' | 'access_role' | 'is_school'
>;

const buildTeamNavItem = (team: NavTeam): MenuItem => ({
  path: `team/${team.id}`,
  text: team.name ?? DEFAULT_TEAM_NAME,
  ...(team.is_school === true && {
    icon: (
      <GraduationCap
        className='~w-3.5/5 h-auto text-gray-500 dark:text-primary-500'
        fill='currentColor'
        fillDarkMode='currentColor'
      />
    )
  })
});

const buildManagedBlockingNavItem = (): MenuItem => ({
  path: ROUTES.MANAGED_BLOCKING,
  text: t('managed_blocking.title'),
  attribAction: ATTRIB.TEST.MANAGED_BLOCKING
});

export const getAccountDropDownMenus = (
  teams: Team[],
  adminForTeams?: AdminForTeams[],
  classes: ClassInfo[] = [],
  areTeamsLoading = false,
  hasManagedBlocking = false
) => {
  let myCircleSubMenus: MenuItem[] = [
    {
      path: ROUTES.ACCOUNTABILITY_BUDDY,
      text: t('accountability_buddy.accountability_buddy'),
      attribAction: ATTRIB.TEST.ACCOUNTABILITY_BUDDY
    },
    // Parents, school admins and managed students only; there is no other way to discover the page.
    ...(hasManagedBlocking ? [buildManagedBlockingNavItem()] : [])
  ];

  // TODO(teacher-mode): remove FE class filter when BE excludes is_class from
  // GET /team-management/admin-teams (see plans/issue-teacher-mode-class-seats-and-admin-teams.md).
  const classIds = new Set(classes.map((classInfo) => classInfo.id));
  const teamsForNav: NavTeam[] = adminForTeams?.length ? adminForTeams : teams;
  const nonClassTeams = teamsForNav.filter(
    (team) => !classIds.has(team.id) && !team.is_class
  );

  // Show Team nav only when admin-teams returned rows (or while that fetch is
  // in flight). Do not use RevenueCat entitlements — stale team_admin grants
  // would otherwise show Team to students.
  const shouldShowTeamNav =
    adminForTeams?.length || nonClassTeams.length > 0 || areTeamsLoading;

  if (shouldShowTeamNav) {
    // Teacher-only users (every row teacher-access) see "My Classes"; any
    // adminship keeps "Team". There is deliberately NO separate Classes nav
    // entry — classes live on the team screen (Part H).
    const hasOnlyTeacherAccess =
      nonClassTeams.length > 0 &&
      nonClassTeams.every(
        (team) => team.access_role === TEAM_ACCESS_ROLE.TEACHER
      );

    myCircleSubMenus = [
      ...myCircleSubMenus,
      {
        path: ROUTES.TEAM,
        text: hasOnlyTeacherAccess ? t('navbar.my_classes') : t('navbar.team'),
        attribAction: ATTRIB.TEST.TEAM,
        isLoading: areTeamsLoading,
        subMenus: nonClassTeams.map(buildTeamNavItem)
      }
    ];
  }

  const extraMenuSubMenus = [
    {
      path: ROUTES.LIBRARY,
      text: t('library'),
      attribAction: ATTRIB.TEST.ACTIVITY_LIBRARY
    },

    {
      path: ROUTES.INTEGRATIONS,
      text: t('to_do_procrastinate.integrations'),
      attribAction: ATTRIB.TEST.INTEGRATIONS
    },
    {
      path: ROUTES.DOWNLOAD_APPS,
      text: t('download_apps'),
      attribAction: ATTRIB.TEST.DOWNLOAD_APPS
    }
  ];

  const profileSubMenus = [
    { path: ROUTES.SECURITY, text: t('account_security.title') },
    {
      path: ROUTES.PROFILE,
      text: t('profile'),
      attribAction: ATTRIB.TEST.PROFILE
    },
    {
      path: ROUTES.MANAGE_SUBSCRIPTION,
      text: t('subscription_page.manage_subscription'),
      attribAction: ATTRIB.TEST.MANAGE_SUBSCRIPTION
    },

    {
      text: t('language'),
      attribAction: ATTRIB.TEST.SELECT_LANGUAGE,
      subMenus: [
        {
          id: PROFILE_LANGUAGE_MENU_ID.EN,
          text: 'EN',
          attribAction: 'select-language-en'
        },
        {
          id: PROFILE_LANGUAGE_MENU_ID.ES,
          text: 'ES',
          attribAction: 'select-language-es'
        }
      ],
      id: PROFILE_SUBMENU_ID.LANGUAGE
    },
    {
      id: PROFILE_SUBMENU_ID.PRIVACY_SECURITY,
      text: t('privacy_security.privacy_and_security'),
      attribAction: ATTRIB.TEST.PRIVACY_SECURITY,
      path: PRIVACY_POLICY.EN
    },
    {
      id: PROFILE_SUBMENU_ID.THEME,
      text: t('theme_toggle.theme'),
      attribAction: ATTRIB.TEST.SELECT_THEME,
      subMenus: [
        {
          id: PROFILE_THEME_MENU_ID.LIGHT,
          text: t('theme_toggle.light_mode'),
          attribAction: 'select-theme-light'
        },
        {
          id: PROFILE_THEME_MENU_ID.DARK,
          text: t('theme_toggle.dark_mode'),
          attribAction: 'select-theme-dark'
        }
      ]
    },
    {
      path: ROUTES.LOG_OUT,
      text: t('logout'),
      attribAction: ATTRIB.TEST.LOGOUT
    }
  ];

  return {
    myCircleSubMenus,
    extraMenuSubMenus,
    profileSubMenus
  };
};
